import React, {useState} from 'react';
import {Button, Modal, Table, Tabs, Tooltip} from 'antd';
import {ApartmentOutlined, BankOutlined, CompassOutlined, TeamOutlined} from '@ant-design/icons';

const OfficeAccessModal = ({visible, data, tabKey, onCancel}) => {
    const [activeTabKey, setActiveTabKey] = useState(tabKey ?? 'offices');

    const officesCount = data?.offices?.length ?? 0;
    const fleetsCount = data?.fleets?.length ?? 0;
    const entitiesCount = data?.accountingCompanies?.length ?? 0;
    const vesselsCount = data?.vesselResponsibilities?.length ?? 0;

    const summaryCards = [
        {key: 'offices',                 label: 'Offices',                 count: officesCount,  icon: <TeamOutlined/>},
        {key: 'fleets',                  label: 'Fleets',                  count: fleetsCount,   icon: <ApartmentOutlined/>},
        {key: 'entities',                label: 'Entities',                count: entitiesCount, icon: <BankOutlined/>},
        {key: 'vesselResponsibilities',  label: 'Vessel Responsibilities', count: vesselsCount,  icon: <CompassOutlined/>},
    ];

    const nameOfficeColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (val) => <Tooltip title={val}><span className="font-medium text-[#0B1220]">{val}</span></Tooltip>,
        },
        {
            title: 'Office',
            dataIndex: 'office',
            key: 'office',
            width: 200,
            render: (val) => <Tooltip title={val}><span className="text-[#374152]">{val ?? '—'}</span></Tooltip>,
        },
    ];

    const entityColumns = [
        {
            title: 'Coy',
            dataIndex: 'coy',
            key: 'coy',
            width: 90,
            render: (val) => <span className="cell-mono">{val}</span>,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (val) => <Tooltip title={val}><span className="font-medium text-[#0B1220]">{val}</span></Tooltip>,
        },
    ];

    const tableProps = (dataSource, columns) => ({
        columns,
        dataSource,
        rowKey: 'key',
        size: 'small',
        pagination: dataSource?.length > 20 ? {pageSize: 20, showSizeChanger: false} : false,
        scroll: {x: 'max-content', y: 380},
    });

    const tabs = [
        {
            key: 'offices',
            children: (
                <Table
                    {...tableProps(
                        data?.offices?.map((item, index) => ({key: index, name: item.name, office: item.office})),
                        nameOfficeColumns
                    )}
                />
            ),
        },
        {
            key: 'fleets',
            children: (
                <Table
                    {...tableProps(
                        data?.fleets?.map((item, index) => ({key: index, name: item.name, office: item.office})),
                        nameOfficeColumns
                    )}
                />
            ),
        },
        {
            key: 'entities',
            children: (
                <Table
                    {...tableProps(
                        data?.accountingCompanies?.map((item, index) => ({key: index, coy: item.id, name: item.name})),
                        entityColumns
                    )}
                />
            ),
        },
        {
            key: 'vesselResponsibilities',
            children: (
                <Table
                    {...tableProps(
                        data?.vesselResponsibilities?.map((item, index) => ({key: index, coy: item.id, name: item.name})),
                        entityColumns
                    )}
                />
            ),
        },
    ];

    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            title={
                <div>
                    <div className="text-[18px] sm:text-[20px] font-semibold text-[#0B1220] leading-tight">
                        Access details
                    </div>
                    <div className="text-[12.5px] sm:text-[13px] text-[#7A849A] mt-1 font-normal">
                        Offices, fleets, entities and vessels you can see and act on.
                    </div>
                </div>
            }
            footer={[
                <Button key="close" onClick={onCancel}>Close</Button>,
            ]}
            closable={true}
            width="min(780px, 95vw)"
            centered
        >
            {/* Summary strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2.5 mb-4 sm:mb-5">
                {summaryCards.map((card) => {
                    const isActive = activeTabKey === card.key;
                    return (
                        <button
                            key={card.key}
                            onClick={() => setActiveTabKey(card.key)}
                            className="flex items-center gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-[10px] border text-left transition-colors min-w-0"
                            style={{
                                backgroundColor: isActive ? '#ECF9EE' : '#F7F8FB',
                                borderColor: isActive ? '#5FC66A' : '#E4E7EE',
                            }}
                        >
                            <div
                                className="w-7 h-7 sm:w-8 sm:h-8 rounded-md flex items-center justify-center text-[14px] sm:text-[16px] flex-shrink-0"
                                style={{
                                    backgroundColor: '#fff',
                                    color: isActive ? '#1B7A31' : '#3F4A63',
                                }}
                            >
                                {card.icon}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-[16px] sm:text-[18px] font-semibold leading-none text-[#0B1220] tabular-nums">
                                    {card.count}
                                </div>
                                <div className="text-[10.5px] sm:text-[11px] text-[#7A849A] mt-1 truncate">{card.label}</div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <Tabs
                activeKey={activeTabKey}
                onChange={setActiveTabKey}
                items={tabs}
                size="middle"
            />
        </Modal>
    );
};

export default OfficeAccessModal;
